import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { GiftCardDetails } from '@models/api-orders.models';

import { CustomAsyncValidatorFn } from '../model/form-elements.model';
import { NotificationSettings } from '@core/services/notification/notification';
import { HttpErrorResponse } from '@angular/common/http';

/**
 * @param checkService class that implements 'checkGiftCardCode' that returns observable
 * @param notificationService class that implements the notifications system
 * @param checkService.checkGiftCardCode
 * @param notificationService.createNotification
 * @returns Observable of null (no errors) or ValidationError (specific keys for error translation)
 */
export const basketGiftCardValidator =
    (
        checkService: {
            checkGiftCardCode: (code: string) => Observable<GiftCardDetails>;
        },
        notificationService: {
            createNotification: (settings: NotificationSettings) => void;
        }
    ): CustomAsyncValidatorFn =>
    control => {
        const code = control.value;
        if (!code) {
            // Does not check for 'required', use separate validator
            return of(null);
        }
        return checkService.checkGiftCardCode(code).pipe(
            // KOP-2389 | we need to take a look on the error code in order to make a custom error
            catchError(err => of(err)),
            map(response => {
                if (response instanceof HttpErrorResponse) {
                    const { status } = response;
                    if (status === 429) {
                        notificationService.createNotification({
                            type: 'error',
                            translationHeadline: 'giftCardCode.notificationHeadline',
                            translationText: 'giftCardCode.notificationText'
                        });
                        return { throttled: true as const };
                    }

                    return { invalidGiftCard: true as const };
                }

                // If there's any 200 response, but the gift card is old or not can be uses on the webshop
                if (response.isBlockedForWeb) {
                    return { isBlockedForWeb: true as const };
                }

                // If there's any 200 response, no errors
                return null;
            })
        );
    };
